import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Clock, Users, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * DayReserveModal — reserve a gym slot for the client straight from the planner,
 * without navigating to the calendar. Lists all of the selected day's slots with
 * live capacity and books the client into the chosen one (trainer-book).
 */
const DayReserveModal = ({ date, clientId, clientName, onClose, onBooked }) => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingId, setBookingId] = useState(null);
  const [error, setError] = useState('');

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/timeslots?date=${date}`, { headers: authHeaders() });
        if (!cancelled) {
          const sorted = (res.data || []).sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
          setSlots(sorted);
        }
      } catch (e) {
        if (!cancelled) setError('Could not load slots for this day');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [date]);

  const reserve = async (slot) => {
    setBookingId(slot.id);
    setError('');
    try {
      await axios.post(`${API}/bookings/trainer-book?slot_id=${slot.id}&client_id=${clientId}`, {}, { headers: authHeaders() });
      onBooked?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not reserve this slot');
      setBookingId(null);
    }
  };

  const prettyDate = (() => {
    try {
      return new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    } catch { return date; }
  })();

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-4" onClick={onClose} data-testid="day-reserve-modal">
      <div className="bg-gray-800 rounded-xl max-w-md w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <div>
            <h3 className="text-white font-bold text-lg">Reserve a slot</h3>
            <p className="text-gray-400 text-sm">{prettyDate}{clientName ? ` · ${clientName}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="day-reserve-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <p className="text-gray-400 text-sm text-center py-6">Loading slots...</p>
          ) : slots.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-6" data-testid="day-reserve-empty">
              No slots exist for this day. Create them in the Calendar first.
            </p>
          ) : (
            slots.map((slot) => {
              const used = slot.current_clients || 0;
              const cap = slot.max_clients || 0;
              const isFull = used >= cap;
              return (
                <div
                  key={slot.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${isFull ? 'bg-gray-900/40 border-gray-700 opacity-60' : 'bg-gray-700/40 border-gray-600/40'}`}
                  data-testid={`reserve-slot-option-${slot.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-blue-300" />
                    <span className="text-white text-sm font-medium">{slot.start_time} – {slot.end_time}</span>
                    <span className={`text-xs flex items-center gap-1 ${isFull ? 'text-amber-300' : 'text-gray-400'}`}>
                      <Users className="w-3 h-3" /> {used}/{cap}
                    </span>
                  </div>
                  <button
                    onClick={() => reserve(slot)}
                    disabled={isFull || bookingId === slot.id}
                    className={`text-xs font-medium px-3 py-1.5 rounded-lg transition ${
                      isFull ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-blue-600/40 text-blue-200 hover:bg-blue-600/60'
                    }`}
                    data-testid={`reserve-slot-confirm-${slot.id}`}
                  >
                    {isFull ? 'Full' : bookingId === slot.id ? 'Reserving...' : 'Reserve'}
                  </button>
                </div>
              );
            })
          )}
          {error && <p className="text-red-300 text-sm bg-red-500/10 border border-red-400/30 rounded p-2">{error}</p>}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DayReserveModal;
